import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

// Shared layout for the better-auth pages. These render bare — no app shell and no header (see
// rootRoute's `noShell` mode) — so the page owns the whole viewport and centres its content in it.
// The Stotles logo sits inside the card, above the heading, rather than in a header bar.
//
// `100dvh` rather than `100vh`: on mobile Safari/Chrome the dynamic unit excludes the collapsing
// browser chrome, so a centred card isn't pushed under it. The `min-height` (not `height`) lets a
// tall card — the register form, or a card showing several validation errors — grow and scroll
// instead of overflowing a fixed box.
export const Page = styled(Flex)({
  boxSizing: "border-box",
  minHeight: "100dvh",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 16px",
});

// 32px between blocks, matching the Auth 2026 designs and the sign-up column, which already uses it.
export const Card = styled(Flex)<{ centred?: boolean }>(({ centred, theme }) => ({
  width: "100%",
  maxWidth: 400,
  flexDirection: "column",
  gap: 32,

  // Inline links, styled here rather than per call site so both router `Link`s and plain anchors
  // pick it up. Left to themselves they fall through to Ant Design's reset, which derives its link
  // colour from `colorPrimary` (blue600) — one step off the blue500 the buttons beside them use.
  // Exclude our own Button's since they manage themselves
  "a:not([data-stotles-button])": {
    color: theme.colors.sysPrimaryDefault,
    textDecoration: "underline",
    textUnderlineOffset: 2,
    "&:hover": { color: theme.colors.sysPrimaryHover },
    "&:active": { color: theme.colors.sysPrimaryPressed },
  },

  ...(centred && {
    textAlign: "center",
    // The lockup is an image inside a link out to the marketing site, so centre either shape — an auto
    // inline margin wins over the `align-self` StotlesLogo sets for the left-aligned cards.
    "& > img, & > a": { marginInline: "auto" },
  }),
}));

export const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

export const Field = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

// Keeps a form field in the DOM but visually hidden, so password managers can autofill username +
// password in one pass across a two-step (identifier-first) flow. Toggle with the `data-hidden`
// attribute. Uses the sr-only clip pattern rather than `display:none`/`visibility:hidden`/`disabled`,
// which suppresses autofill. Pair with `tabIndex={-1}` + `aria-hidden` on the input itself so
// keyboard/AT skip it while it's collapsed.
export const CollapsedField = styled(Field)({
  "&[data-hidden]": {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
});
